import Header from 'components/molecules/Header';
import { OrderStepper } from 'components/molecules/Stepper';
import { useConfig } from 'context/app-config';
import useFlags from 'hooks/useFlags';
import CheckoutPage from 'packages/app/checkout';
import AddAddressPage from 'packages/app/newOrder/modules/AddAddress';
import EditAddressPage from 'packages/app/newOrder/modules/EditAddress';
import PickupDropoff from 'packages/shared/newOrder/components/PickupDropoff';
import useOrderStep from 'packages/shared/newOrder/hooks/useOrderStep';
import OrderInstructions from 'packages/shared/newOrder/modules/OrderInstructions';
import { selectSelectedServices } from 'pages/newOrder/newOrder.selectors';
import { clearState, setDefaultServiceLine } from 'pages/newOrder/newOrder.slice';
import { useCustomerPreferences } from 'pages/queries/user.queries';
import React, { useCallback, useEffect, useMemo } from 'react';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { selectCustomer } from 'redux-stores/slices/authSlice';
import styled from 'styled-components';
import { useBasePath } from 'utils/router/hooks/useBasePath';

const StyledContainer = styled(Container)`
  padding-bottom: 120px;
`;

export const Stepper: React.FC = () => {
  const basePath = useBasePath();
  const navigate = useNavigate();
  const selectedServiceLines = useSelector(selectSelectedServices);
  const dispatch = useDispatch();
  const { activeStep, decrementStep } = useOrderStep();
  const customer = useSelector(selectCustomer);
  const config = useConfig();
  const { enableSelectServicesFlow } = useFlags();

  useCustomerPreferences();

  useEffect(() => {
    if (selectedServiceLines.length === 0 && config?.modules.newOrder.dropoffs?.hasSupportForMultipleDropoffs) {
      // incase user direct opens the new order page
      dispatch(setDefaultServiceLine());
    }
  }, [config?.modules.newOrder.dropoffs?.hasSupportForMultipleDropoffs, dispatch, selectedServiceLines.length]);

  const handleClose = useCallback(() => {
    dispatch(clearState());
    navigate('/home');
  }, [dispatch, navigate]);

  const onGoBackClicked = useCallback(() => {
    if (activeStep > 0) {
      decrementStep();
    } else {
      handleClose();
    }
  }, [activeStep, decrementStep, handleClose]);

  const renderOrderModule = React.useCallback(() => {
    if (!customer) return null;

    if (enableSelectServicesFlow) {
      switch (activeStep) {
        case 0:
          return <PickupDropoff />;
        case 1:
          return <CheckoutPage />;
        default:
          return null;
      }
    }

    switch (activeStep) {
      case 0:
        return <PickupDropoff />;
      case 1:
        return <OrderInstructions />;
      case 2:
        return <CheckoutPage />;
      default:
        return null;
    }
  }, [activeStep, customer, enableSelectServicesFlow]);

  const FixedHeader = useMemo(
    () => (
      <>
        <Row>
          <Header isShowSupportOption isShowPricingOption={!config?.hidePricingTopBarButton} onGoBackClicked={onGoBackClicked} />
        </Row>
        <Row>
          <Col xs={12}>
            <OrderStepper activeStep={activeStep} />
          </Col>
        </Row>
      </>
    ),
    [activeStep, onGoBackClicked, config?.hidePricingTopBarButton]
  );

  // redirect to fix payment if customer has a failed-payment order
  if (customer?.pendingOrders && customer.pendingOrders.length > 0) {
    const failedOrder = customer.pendingOrders.find(({ status }) => Number(status) === 6.5);
    if (failedOrder) {
      return <Navigate to={`/order/details/active/${failedOrder.id}`} replace />;
    }
  }

  return (
    <Routes>
      <Route path="addAddress/*" element={<AddAddressPage />} />
      <Route path="editAddress/*" element={<EditAddressPage />} />
      <Route
        path="flex"
        element={
          <StyledContainer>
            {FixedHeader}
            {renderOrderModule()}
          </StyledContainer>
        }
      />
      <Route index element={<Navigate to={`/${basePath}/flex`} />} />
    </Routes>
  );
};

export default Stepper;
